import React from "react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { PageHeader, SectionCard, Kpi, CardSkeleton, EmptyState, TabList } from "@/admin/ui";
import { TrendingUp, TrendingDown, Users, CreditCard, AlertTriangle, RefreshCw, Activity, ShieldAlert } from "lucide-react";

const fmt$ = (cents) => `$${(Number(cents || 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`;
const fmtN = (n) => Number(n || 0).toLocaleString();

function RevenueChart({ series }) {
  if (!series?.length) return <EmptyState title="No revenue data for this window" />;
  const w = 720, h = 160, pad = 24;
  const maxCents = Math.max(...series.map((d) => d.gross_cents), 1);
  const xs = (i) => pad + (i * (w - 2 * pad)) / (series.length - 1 || 1);
  const ys = (c) => h - pad - ((c / maxCents) * (h - 2 * pad));
  const path = series.map((d, i) => `${i === 0 ? "M" : "L"}${xs(i).toFixed(1)},${ys(d.gross_cents).toFixed(1)}`).join(" ");
  const rpath = series.map((d, i) => `${i === 0 ? "M" : "L"}${xs(i).toFixed(1)},${ys(d.refunded_cents).toFixed(1)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-40" data-testid="admin-revenue-chart">
      <path d={path} stroke="#e0b04a" strokeWidth="2" fill="none" />
      <path d={rpath} stroke="#c94a4a" strokeWidth="1.5" strokeDasharray="4 3" fill="none" />
    </svg>
  );
}

export default function AdminOverview() {
  const { t } = useLang();
  const [days, setDays] = React.useState(30);
  const [data, setData] = React.useState(null);
  const [me, setMe] = React.useState(null);
  const [action, setAction] = React.useState(null);
  const load = React.useCallback(() => {
    api.get(`/api/admin/dashboard?days=${days}`).then((r) => setData(r.data));
    api.get("/api/admin/action-center").then((r) => setAction(r.data));
  }, [days]);
  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => { api.get("/api/admin/me").then((r) => setMe(r.data)); }, []);

  const ranges = [7, 30, 90, 365];
  const rangeControls = (
    <div className="flex gap-1 flex-wrap" role="group" aria-label="Date range">
      {ranges.map((r) => (
        <button key={r} onClick={() => setDays(r)}
          className={`text-xs px-2.5 py-1 rounded ${days === r ? "bg-white/15 text-white" : "text-white/60 hover:bg-white/5"}`}
          data-testid={`admin-range-${r}`}>{r}{t("admin.dashboard.days_abbrev")}</button>
      ))}
      <button className="lp-btn-ghost" onClick={load} data-testid="admin-refresh" aria-label="Refresh"><RefreshCw className="w-3.5 h-3.5" /></button>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-overview-page">
      <PageHeader
        title={t("admin.dashboard.title")}
        description={me ? `${me.email} · ${(me.admin_role || "").replace(/_/g, " ")}` : ""}
        meta={`${t("admin.dashboard.range")}: ${days}${t("admin.dashboard.days_abbrev")}`}
        primary={rangeControls}
      />

      {!data ? <CardSkeleton h="h-32" /> : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label={t("admin.kpi.gross")} value={fmt$(data.kpis.gross_cents)} icon={TrendingUp} testid="admin-kpi-gross" />
          <Kpi label={t("admin.kpi.refunded")} value={fmt$(data.kpis.refunded_cents)} tone="danger" icon={TrendingDown} testid="admin-kpi-refunded" />
          <Kpi label={t("admin.kpi.net")} value={fmt$(data.kpis.net_cents)} tone="success" testid="admin-kpi-net" />
          <Kpi label={t("admin.kpi.active_subs")} value={fmtN(data.kpis.active_subscriptions)} testid="admin-kpi-subs" />
          <Kpi label={t("admin.kpi.users_total")} value={fmtN(data.kpis.users_total)} sub={`+${fmtN(data.kpis.users_new)} ${t("admin.kpi.new")}`} icon={Users} testid="admin-kpi-users" />
          <Kpi label={t("admin.kpi.paying")} value={fmtN(data.kpis.paying_users)} testid="admin-kpi-paying" />
          <Kpi label={t("admin.kpi.credits_sold")} value={fmtN(data.kpis.credits_sold)} sub={`${fmtN(data.kpis.credits_spent)} ${t("admin.kpi.spent")}`} icon={CreditCard} testid="admin-kpi-credits" />
          <Kpi label={t("admin.kpi.credits_outstanding")} value={fmtN(data.kpis.credits_outstanding)} tone="warning" testid="admin-kpi-outstanding" />
          <Kpi label={t("admin.kpi.open_disputes")} value={fmtN(data.kpis.open_disputes)} tone={data.kpis.open_disputes ? "danger" : undefined} icon={AlertTriangle} testid="admin-kpi-disputes" />
          <Kpi label={t("admin.kpi.open_reviews")} value={fmtN(data.kpis.open_reviews)} tone={data.kpis.open_reviews ? "warning" : undefined} testid="admin-kpi-reviews" />
          <Kpi label={t("admin.kpi.failed_webhooks")} value={fmtN(data.kpis.failed_webhooks)} tone={data.kpis.failed_webhooks ? "danger" : undefined} testid="admin-kpi-failed-hooks" />
          <Kpi label={t("admin.kpi.frozen_users")} value={fmtN(data.kpis.frozen_users)} tone={data.kpis.frozen_users ? "warning" : undefined} testid="admin-kpi-frozen" />
        </div>
      )}

      <SectionCard title={t("admin.dashboard.revenue_trend")} description={`Last ${days} ${t("admin.dashboard.days_abbrev")}`}
        right={<div className="text-xs text-white/50 flex items-center gap-3">
          <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-amber-400" />{t("admin.kpi.gross")}</span>
          <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-red-400" />{t("admin.kpi.refunded")}</span>
        </div>}>
        {!data ? <CardSkeleton h="h-40" /> : <RevenueChart series={data.revenue_series} />}
      </SectionCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ActionList title={t("admin.action.reviews")} items={action?.open_reviews} icon={AlertTriangle} testid="action-reviews"
          renderItem={(c) => (
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-white/85 text-sm truncate">{c.kind}</div>
                <div className="text-white/40 text-xs">{new Date(c.created_at).toLocaleString()}</div>
              </div>
              <div className="text-amber-300 text-xs tabular-nums shrink-0">{fmt$(c.amount_cents || 0)}</div>
            </div>
          )} />
        <ActionList title={t("admin.action.disputes")} items={action?.open_disputes} icon={ShieldAlert} testid="action-disputes"
          renderItem={(d) => (
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-white/85 text-sm truncate">{d.reason || d.normalized_status}</div>
                <div className="text-white/40 text-xs">{new Date(d.created_at).toLocaleString()}</div>
              </div>
              <div className="text-red-300 text-xs tabular-nums shrink-0">{fmt$(d.amount_cents || 0)}</div>
            </div>
          )} />
        <ActionList title={t("admin.action.failed_webhooks")} items={action?.failed_webhooks} icon={Activity} testid="action-failed-hooks"
          renderItem={(w) => (
            <div className="min-w-0">
              <div className="text-white/85 text-sm font-mono truncate">{w.event_type}</div>
              <div className="text-red-300 text-xs truncate">{w.error?.slice(0, 100)}</div>
            </div>
          )} />
        <ActionList title={t("admin.action.frozen")} items={action?.frozen_users} icon={Users} testid="action-frozen"
          renderItem={(u) => (
            <div className="flex items-center justify-between gap-2">
              <div className="text-white/85 text-sm truncate">{u.email}</div>
              <div className="text-amber-300 text-xs truncate">{u.topup_frozen_reason}</div>
            </div>
          )} />
      </div>
    </div>
  );
}

function ActionList({ title, items, renderItem, icon: Icon, testid }) {
  const { t } = useLang();
  const arr = items || [];
  return (
    <SectionCard title={title} testid={testid}
      right={<span className="text-white/40 text-xs">{arr.length}</span>}>
      {arr.length === 0 ? (
        <EmptyState icon={Icon} title={t("admin.action.empty")} />
      ) : (
        <ul className="space-y-2 max-h-72 overflow-y-auto">
          {arr.slice(0, 8).map((it, i) => (
            <li key={it.id || it.event_id || i} className="border-b border-white/5 pb-2 last:border-b-0">
              {renderItem(it)}
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}
